import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CollectionSidebar from './Sidebar';
import { useKeyboardCommands } from '@/shared/hooks/useKeyboardCommands';
import { Button } from '@/shared/components/ui/button';
import { ArrowUp } from 'lucide-react';
import { useCollectionSelection } from '@/features/collections/hooks/useCollectionSelection';
import { usePageSelection } from '@/features/pages/hooks/usePageSelection';

type CollectionLayoutProps = {
  children: ReactNode;
  Menu?: ReactNode;
};

const CollectionLayout = ({ children, Menu }: CollectionLayoutProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const { pageId, collectionId } = useParams<{ pageId: string; collectionId: string }>();
  const { setCollection } = useCollectionSelection();
  const { setCurrentPageId } = usePageSelection();
  useKeyboardCommands();

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchCollection = async () => {
      if (!collectionId) {
        navigate(`/collections`);
        return;
      }

      const collection = await setCollection(collectionId);
      if (!collection) {
        await setCollection(null);
        navigate(`/collections`);
        void setCurrentPageId(null);
        return;
      }
      if (pageId && collectionId) {
        navigate(`/collection/${collectionId}/page/${pageId}`);
        return;
      }
      const firstPage =
        collection.structure.length &&
        collection.structure[0].section.pages.length &&
        collection.structure[0].section.pages[0];
      if (firstPage && !pageId) {
        navigate(`/collection/${collectionId}/page/${firstPage.id}`);
      }
    };
    void fetchCollection();
  }, [collectionId, navigate, pageId, setCollection, setCurrentPageId]);

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToTop();
        }, 100);
      });
    }
  }, [pageId, collectionId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollButton(container.scrollTop > 400);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="flex h-screen">
      <CollectionSidebar
        onBackClick={() => {
          navigate('/collections');
        }}
      />
      <main ref={containerRef} className="flex-1 overflow-y-auto relative overflow-x-hidden">
        {Menu}
        {children}
        {showScrollButton && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-4 right-4 h-8 w-8 p-0 z-1000"
            variant="secondary"
            size="icon"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
      </main>
    </div>
  );
};

export default memo(CollectionLayout);
